/**
 * Data Mapping Utility
 * Ensures a consistent data shape for UI components, providing sensible defaults
 * for missing data to prevent crashes and improve UI stability.
 */

/**
 * Normalize a single block document from the backend to a consistent format
 * that UI components can safely consume.
 *
 * @param {Object} block - Raw block data from the API.
 * @returns {Object} - Normalized block data for UI consumption.
 */
export const normalizeBlockData = (block) => {
  if (!block) return {};
  
  // Start with the original block data
  const normalized = { ...block };
  
  // --- ENSURE CRITICAL FIELDS EXIST ---
  // These defaults prevent UI components from crashing if data is missing.
  normalized.serial = normalized.serial || normalized.id || 'Unknown';
  normalized.status = normalized.status || 'critical';
  normalized.is_online = normalized.is_online !== undefined ? normalized.is_online : (normalized.cloud_connected || false);
  normalized.sensors_healthy = normalized.sensors_healthy !== undefined ? normalized.sensors_healthy : true;
  normalized.last_seen = normalized.last_seen || normalized.lastHeartbeat || null;
  
  // --- ENSURE IMAGE FIELDS EXIST ---
  normalized.images = normalized.images || {};
  normalized.image = normalized.image || normalized.images.profile || normalized.images.primary || null;
  
  // --- ENSURE ARRAYS EXIST ---
  normalized.dispense_history = Array.isArray(normalized.dispense_history) ? normalized.dispense_history : [];
  normalized.activeErrors = Array.isArray(normalized.activeErrors) ? normalized.activeErrors : (Array.isArray(normalized.errors) ? normalized.errors : []);
  normalized.activeWarnings = Array.isArray(normalized.activeWarnings) ? normalized.activeWarnings : (Array.isArray(normalized.warnings) ? normalized.warnings : []);
  
  // --- ENSURE SLOTS ARRAY EXISTS ---
  // This is critical for sunscreen management UI to work properly
  normalized.slots = Array.isArray(normalized.slots) ? normalized.slots : [];
  
  // If no slots exist, create default 2-slot configuration for new devices
  if (normalized.slots.length === 0 && normalized.serial && normalized.serial !== 'Unknown') {
    // Create default 2-slot configuration
    normalized.slots = [
      { 
        index: 0, 
        status: 'disabled', 
        spf: null, 
        brand: null, 
        fill_level: 0, 
        type: null,
        enabled: false
      },
      { 
        index: 1, 
        status: 'disabled', 
        spf: null, 
        brand: null, 
        fill_level: 0, 
        type: null,
        enabled: false
      }
    ];
  }
  
  // --- CALCULATE DERIVED VALUES for UI ---
  // This is where we can add frontend-specific calculations without altering backend logic.
    normalized.hasErrors = normalized.activeErrors.length > 0;
    normalized.hasWarnings = normalized.activeWarnings.length > 0;

  // Example: Calculate a display-friendly temperature
  if (normalized.temperature_f !== undefined) {
      normalized.displayTemp = `${parseFloat(normalized.temperature_f).toFixed(1)}°F`;
  } else {
      normalized.displayTemp = 'N/A';
  }
  
  // Example: Consolidate battery percentage
  normalized.battery_soc = normalized.battery_soc !== undefined ? normalized.battery_soc : (normalized.battery || 0);

  
  return normalized;
};

/**
 * Normalize an array of blocks.
 * @param {Array} blocks - Array of raw block data.
 * @returns {Array} - Array of normalized block data.
 */
export const normalizeBlocksData = (blocks) => {
  if (!Array.isArray(blocks)) return [];
  return blocks.map(normalizeBlockData);
};

/**
 * Get aggregated business stats from normalized blocks
 * @param {Array} blocks - Array of normalized blocks
 * @returns {Object} - Business statistics
 */
export const calculateBusinessStats = (blocks) => {
  return blocks.reduce((stats, block) => {
    stats.dollarsEarned += block.revenue || 0;
    stats.customersServed += block.dispensesLifetime || 0;
    stats.inchesProtected += (block.dispensesLifetime || 0) * 2; // 2 inches per dispense
    return stats;
  }, {
    dollarsEarned: 0,
    customersServed: 0,
    inchesProtected: 0
  });
};

/**
 * Get dispense history stats from a block
 * @param {Object} block - Normalized block data
 * @returns {Object} - Dispense statistics
 */
export const getDispenseStats = (block) => {
  return {
    totalDispenses: block.dispensesLifetime || 0,
    lastDispenseTime: block.lastDispenseTime || null,
    fillLevel: block.currentLevel || 0,
    fillLevelMl: block.fill_level_ml || 0,
    revenue: block.revenue || 0
  };
};

export default {
  normalizeBlockData,
  normalizeBlocksData,
  calculateBusinessStats,
  getDispenseStats
}; 